import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
  MenuItem,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaletteIcon from '@mui/icons-material/Palette';
import StorageIcon from '@mui/icons-material/Storage';
import LanguageIcon from '@mui/icons-material/Language';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import { auth } from '../firebase';

function Settings() {
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    // Profile
    displayName: auth.currentUser?.email?.split('@')[0] || '',
    email: auth.currentUser?.email || '',
    phone: '',
    timezone: 'America/New_York',
    
    // Notifications
    emailNotifications: true,
    caseUpdates: true,
    deadlineReminders: true,
    teamActivity: false,
    aiSuggestions: true,
    
    // AI Preferences
    aiModel: 'gemini-pro',
    confidenceThreshold: 0.7,
    autoSave: true,
    
    // Interface
    theme: 'light',
    language: 'en',
    compactMode: false,
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account and application preferences
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                Profile Settings
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mr: 2
                  }}
                >
                  {settings.displayName[0]?.toUpperCase()}
                </Avatar>
                <Button variant="outlined" startIcon={<EditIcon />}>
                  Change Photo
                </Button>
              </Box>

              <TextField
                fullWidth
                label="Display Name"
                value={settings.displayName}
                onChange={(e) => handleChange('displayName', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Email"
                value={settings.email}
                disabled
                margin="normal"
                helperText="Email cannot be changed"
              />
              <TextField
                fullWidth
                label="Phone Number"
                value={settings.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                margin="normal"
                placeholder="+1 (555) 123-4567"
              />
              <TextField
                fullWidth
                select
                label="Timezone"
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                margin="normal"
              >
                <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
              </TextField>
            </CardContent>
          </Card>
        </Grid>

        {/* Preferences */}
        <Grid item xs={12} md={6}>
          {/* Notifications */}
          <Card sx={{ boxShadow: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon color="primary" />
                Notifications
              </Typography>

              <List>
                <ListItem>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive email updates"
                  />
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Case Updates"
                    secondary="Notify on case changes"
                  />
                  <Switch
                    checked={settings.caseUpdates}
                    onChange={(e) => handleChange('caseUpdates', e.target.checked)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Deadline Reminders"
                    secondary="Alert before deadlines"
                  />
                  <Switch
                    checked={settings.deadlineReminders}
                    onChange={(e) => handleChange('deadlineReminders', e.target.checked)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Team Activity"
                    secondary="Updates from team members"
                  />
                  <Switch
                    checked={settings.teamActivity}
                    onChange={(e) => handleChange('teamActivity', e.target.checked)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="AI Suggestions"
                    secondary="Receive AI recommendations"
                  />
                  <Switch
                    checked={settings.aiSuggestions}
                    onChange={(e) => handleChange('aiSuggestions', e.target.checked)}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
        <Button variant="outlined" size="large">
          Cancel
        </Button>
        <Button variant="contained" size="large" startIcon={<SaveIcon />} onClick={handleSave}>
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default Settings;